import { AgGridReact } from "ag-grid-react";
import { useMemo, useState } from "react";
import { AllCommunityModule, ColDef, ModuleRegistry } from "ag-grid-community";
import {
    RowGroupingModule,
    RowGroupingPanelModule,
    MasterDetailModule,
    StatusBarModule,
} from "ag-grid-enterprise";
import clsx from "clsx";
import { useInnerTableParams } from "./InnerTable";
import metricData from "../lineageMetrics.transformed.json";
import { atom } from "jotai";
import { VariableIcon } from "@heroicons/react/24/outline";
import { LicenseManager } from "ag-grid-enterprise";
import { AccountLabel } from "@/entities/Labeling/Account.model";
import {
    getAccounts,
    getLineageMetrics,
} from "@/entities/Finance/Finance.queries";
import { useApiQuery } from "@/api/hooks/useApiQuery";
import {
    HydratedLineageMetric,
    HydratedLineagedValue,
} from "@/entities/Finance/Finance.model";
import { investmentMemoGridTheme } from "./agGridTheme";
import RightSidebarButton from "@/features/Navigation/components/RightSidebarButton";

LicenseManager.setLicenseKey(process.env.AG_LICENSE_KEY || "");

ModuleRegistry.registerModules([
    AllCommunityModule,
    RowGroupingModule,
    RowGroupingPanelModule,
    MasterDetailModule,
    StatusBarModule,
]);

const onFirstDataRendered = (params: any) => {
    params.api.expandAll();
};

export const openRowChildrenCount = atom<number>(0);

export function LineageTable() {
    const metrics = useApiQuery(getLineageMetrics, {
        params: { labels: [AccountLabel.REVENUES] },
    });
    console.log(metrics.data, metrics.error);
    const accounts = useApiQuery(getAccounts);
    console.log(accounts.data, accounts.error);

    const hydratedMetrics: HydratedLineageMetric[] = useMemo(() => {
        return (
            metrics.data?.map((metric) => {
                return {
                    ...metric,
                    lineagedValues: metric.lineagedValues.map(
                        (lineagedValue) => ({
                            ...lineagedValue,
                            accounts: lineagedValue.accounts.map((account) => ({
                                ...(accounts.data?.[account.accountId] as {
                                    id: string;
                                    name: string;
                                    label: string;
                                }),
                                value: account.value,
                                dataAssetId: account.dataAssetId,
                            })),
                        }),
                    ),
                };
            }) ?? []
        );
    }, [metrics.data, accounts.data]);

    const InnerTableParams = useInnerTableParams();
    const [showFormula, setShowFormula] = useState(false);
    const [columnDefs] = useState<ColDef[]>([
        // group cell renderer needed for expand / collapse icons
        { field: "period", resizable: false },
        {
            field: "value",
            cellRenderer: "agGroupCellRenderer",
            resizable: false,
            maxWidth: 200,
            pinned: "right",
            valueFormatter: ({ value }) => {
                return value
                    ? new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                      }).format(value)
                    : "";
            },
        },
    ]);
    const defaultColDef = useMemo<ColDef>(() => {
        return {
            flex: 1,
        };
    }, []);

    const innerOperands = useMemo(() => {
        const formula = metricData.data[0].description.formula;
        const functionMatch = formula.match(/(\w+)\s*\(([\s\S]*)\)/);

        if (!functionMatch) {
            return [];
        }

        const [, , innerContent] = functionMatch;

        return innerContent
            .split(",")
            .map((operand) => operand.trim())
            .filter((operand) => operand && operand !== "");
    }, [metricData.data[0].description.formula]);

    const statusBar = useMemo(() => {
        return {
            statusPanels: [
                {
                    statusPanel: "agAggregationComponent",
                    statusPanelParams: {
                        // possible values are: 'count', 'sum', 'min', 'max', 'avg'
                        aggFuncs: ["avg", "sum"],
                    },
                    align: "right",
                },
            ],
        };
    }, []);

    return (
        <div className={clsx("bg-default", "flex size-full flex-col gap-4")}>
            <div className="pt-4">
                <RightSidebarButton label="Close" iconDirection="right" />
            </div>
            <div className="size-full">
                <h1 className="pageheading mb-1">Total Revenue (LTM)</h1>
                <p className="subdued mb-2 font-[300] italic">
                    {metricData.data[0].description.description}
                </p>
                <p
                    className={clsx(
                        "mb-2",
                        "body-micro subdued flex cursor-pointer items-center gap-1",
                    )}
                    onClick={() => setShowFormula(!showFormula)}
                >
                    {showFormula ? "Hide formula" : "Show formula"}{" "}
                    <VariableIcon className="size-4" color="#E5E7EB" />
                </p>
                {showFormula && (
                    <div
                        className={clsx(
                            " bg-[#263142] p-5",
                            "flex flex-wrap",
                            "gap-2 rounded",
                        )}
                    >
                        {innerOperands.map((line) => {
                            return (
                                <p className="py-1 font-[300]" key={line}>
                                    {line +
                                        (line ===
                                        innerOperands[innerOperands.length - 1]
                                            ? ""
                                            : " +")}
                                </p>
                            );
                        })}
                    </div>
                )}
                <div className="mt-6 size-full">
                    <AgGridReact<HydratedLineagedValue>
                        theme={investmentMemoGridTheme}
                        rowData={hydratedMetrics?.[0]?.lineagedValues
                            .sort((a, b) => a.period.localeCompare(b.period))
                            .slice(-12)
                            .reverse()}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        masterDetail={true}
                        detailCellRendererParams={InnerTableParams}
                        onFirstDataRendered={onFirstDataRendered}
                        detailRowAutoHeight
                        onRowGroupOpened={(params) => {
                            // Call the function to handle the single-group-open logic
                            const api = params.api;
                            const currentRowNode = params.node;

                            api.forEachNode((node) => {
                                if (node.id !== currentRowNode.id) {
                                    console.log(node.id, currentRowNode.id);
                                    node.setExpanded(false);
                                }
                            });
                        }}
                        statusBar={statusBar}
                        domLayout="autoHeight" //only do this if the table has a limited number of rows
                    />
                </div>
            </div>
        </div>
    );
}
